import * as Yup from "yup";

import {
  AcceptOfferParticipant,
  AcceptOfferParticipantVariables,
} from "_graphql/mutation/__generated__/AcceptOfferParticipant";
import {
  acceptOfferParticipation,
  rejectOfferParticipation,
} from "_graphql/visal/mutations/publish-offer";
import { useKEKOffer, useOffer } from "hooks/data/use-offers";

import { AcceptPercentageForm } from "components/forms";
import { LocationGenerics } from "router/location";
import { Modal } from "components";
import toast from "react-hot-toast";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMatch } from "react-location";
import { useMutation } from "@apollo/client";

export const validationSchema = Yup.object().shape({
  offered: Yup.number().min(0).required(),
  percentage: Yup.number()
    .min(0, "Percentage must be greater than 0")
    .max(Yup.ref("expected"), "Percentage cannot exceed offered percentage")
    .typeError("Must be a number")
    .required("Percentage is required"),
  expected: Yup.number().min(0).required(),
});

export default function AcceptOfferContainer({
  open,
  setOpen,
  reinsurerData,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  reinsurerData?: any;
  refetch?: () => void;
}) {
  const { params } = useMatch<LocationGenerics>();
  const { loading: loadingOffer, offer } = useKEKOffer({
    offer_id: params?.offer || "",
  });

  const [acceptOffer, { loading: acceptLoading }] = useMutation<
    AcceptOfferParticipant,
    AcceptOfferParticipantVariables
  >(acceptOfferParticipation, {
    onCompleted: () => {
      toast.success("Offer accepted successfully");
      refetch?.();
      form.resetForm();
      setOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to accept offer");
    },
    refetchQueries: ["GetOfferPublishedParticipants"],
  });

  const [rejectOffer, { loading: rejectLoading }] = useMutation(
    rejectOfferParticipation,
    {
      onCompleted: () => {
        toast.success("Offer rejected successfully");
        refetch?.();
        form.resetForm();
        setOpen(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to reject offer");
      },
    }
  );

  const loading = acceptLoading || rejectLoading;

  const form = useFormik<any>({
    initialValues: {
      percentage: 0,
      offered: 0,
      expected: 0,
    },
    validationSchema,
    onSubmit: async (variables) => {
      if (!reinsurerData?.offer_published_participant_id) {
        toast.error("No participant data found");
        return;
      }

      const facultative_offer = offer?.facultative_offer || 0;
      if (variables.percentage > facultative_offer) {
        toast.error(
          `Percentage cannot exceed the facultative offer (${facultative_offer}%)`
        );
        return;
      }

      await acceptOffer({
        variables: {
          input: {
            offer_published_participant_id:
              reinsurerData.offer_published_participant_id,
            // offer_participant_percentage: parseFloat(variables.percentage),
          },
        },
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (reinsurerData) {
      form.setFieldValue(
        "offered",
        parseFloat(reinsurerData?.offer_participant_percentage || "0")
      );
      form.setFieldValue(
        "facultative_offer",
        parseFloat(reinsurerData?.offer?.facultative_offer || "0")
      );
      form.setFieldValue(
        "percentage",
        parseFloat(reinsurerData?.offer_participant_percentage || "0")
      );

      // Set expected to the facultative offer from the offer object
      const facultative_offer = offer?.facultative_offer || 0;
      form.setFieldValue("expected", facultative_offer);
    }
  }, [reinsurerData, offer]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={`Respond to Offer from ${
        reinsurerData?.reinsurer?.re_company_name || "Reinsurer"
      }`}
      loading={loadingOffer}
      description="You can accept, modify, or revoke the percentage offered by this reinsurer"
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={() => form.handleSubmit()}
          >
            {acceptLoading ? "Accepting..." : "Accept"}
          </button>
        </>
      )}
    >
      <AcceptPercentageForm form={form} labelText="Percentage to accept" />
    </Modal>
  );
}
